import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaChevronLeft, FaChevronRight, FaPlay, FaPause } from "react-icons/fa";
import Slider1 from "../images/t3.jpg";
import Slider2 from "../images/t9.jpg";
import Slider3 from "../images/t6.jpg";

/* ── Slide data ─────────────────────────────────────── */
const SLIDES = [
  {
    key: "fleet",
    img: Slider1,
    eyebrow: "Fleet Intelligence",
    title: "Vehicle Tracking &",
    titleAccent: "Fleet Management",
    desc: "Advanced GPS-based systems that provide real-time visibility into vehicle movement, fuel usage, driver behaviour, and route optimization — enabling safer, more efficient transport operations.",
    cta: { label: "Explore Fleet Solutions", to: "/service" },
    ctaSecondary: { label: "Watch Demo", to: "/technology" },
    align: "left",
  },
  {
    key: "analytics",
    img: Slider2,
    eyebrow: "Data & Insights",
    title: "Analytics &",
    titleAccent: "Reporting Tools",
    desc: "Powerful dashboards and data visualization tools that transform raw operational data into actionable insights — helping organizations make faster, smarter decisions.",
    cta: { label: "See Our Analytics", to: "/service" },
    ctaSecondary: { label: "View Case Studies", to: "/about" },
    align: "center",
  },
  {
    key: "software",
    img: Slider3,
    eyebrow: "Tailored Engineering",
    title: "Custom Software",
    titleAccent: "Solutions",
    desc: "Whether integrating with existing ERPs or building standalone platforms, we architect and deliver applications precisely tailored to your business requirements and workflows.",
    cta: { label: "Start a Project", to: "/contact" },
    ctaSecondary: { label: "Our Technology", to: "/technology" },
    align: "left",
  },
];

const INTERVAL = 6000;

/* ── Component ─────────────────────────────────────── */
function Slider() {
  const [current,   setCurrent]   = useState(0);
  const [prev,      setPrev]      = useState(null);
  const [animating, setAnimating] = useState(false);
  const [paused,    setPaused]    = useState(false);
  const [visible,   setVisible]   = useState(false);
  const timerRef = useRef(null);

  /* entrance animation on mount */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  /* auto-advance */
  const goTo = useCallback((next) => {
    if (animating || next === current) return;
    setAnimating(true);
    setPrev(current);
    setCurrent(next);
    setTimeout(() => { setPrev(null); setAnimating(false); }, 700);
  }, [animating, current]);

  const goNext = useCallback(() => goTo((current + 1) % SLIDES.length), [goTo, current]);
  const goPrev = useCallback(() => goTo((current - 1 + SLIDES.length) % SLIDES.length), [goTo, current]);

  useEffect(() => {
    if (paused) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(goNext, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [paused, goNext]);

  /* keyboard navigation */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft")  goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goNext, goPrev]);

  const slide = SLIDES[current];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root {
          --sl-accent:       #00c2ff;
          --sl-accent2:      #7c3aed;
          --sl-overlay-from: rgba(5,15,35,0.82);
          --sl-overlay-to:   rgba(5,15,35,0.30);
          --font-display:    'Syne', sans-serif;
          --font-body:       'DM Sans', sans-serif;
        }

        /* ── Shell ── */
        .sl-root {
          position: relative;
          width: 100%;
          height: 100vh;
          min-height: 560px;
          max-height: 900px;
          overflow: hidden;
          background: #050f23;
          font-family: var(--font-body);
        }

        /* ── Slide layers ── */
        .sl-slide {
          position: absolute;
          inset: 0;
          z-index: 0;
          opacity: 0;
          transition: opacity 0.7s ease;
        }
        .sl-slide.entering { opacity: 1; z-index: 1; }
        .sl-slide.leaving  { opacity: 0; z-index: 0; }

        /* background image */
        .sl-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          transform: scale(1.06);
          transition: transform 7s ease;
        }
        .sl-slide.entering .sl-bg { transform: scale(1); }

        /* overlay */
        .sl-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            105deg,
            var(--sl-overlay-from) 0%,
            var(--sl-overlay-to) 100%
          );
        }
        /* Extra bottom vignette for controls readability */
        .sl-vignette {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(5,15,35,0.7) 0%, transparent 40%);
        }

        /* ── Content ── */
        .sl-content-wrap {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          padding: 0 6vw;
          padding-bottom: 90px;
        }
        .sl-content-wrap.center { justify-content: center; text-align: center; }
        .sl-content-wrap.left   { justify-content: flex-start; text-align: left; }

        .sl-content {
          max-width: 680px;
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.65s ease 0.2s, transform 0.65s ease 0.2s;
        }
        .sl-content.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* eyebrow */
        .sl-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--sl-accent);
          background: rgba(0,194,255,0.1);
          border: 1px solid rgba(0,194,255,0.25);
          padding: 0.3rem 0.9rem;
          border-radius: 50px;
          margin-bottom: 1.1rem;
        }
        .sl-eyebrow::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--sl-accent);
          animation: sl-pulse 1.8s infinite;
        }
        @keyframes sl-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }

        /* heading */
        .sl-title {
          font-family: var(--font-display);
          font-size: clamp(2rem, 5vw, 3.4rem);
          font-weight: 800;
          color: #fff;
          line-height: 1.1;
          margin: 0 0 0.1rem;
        }
        .sl-title-accent {
          font-family: var(--font-display);
          font-size: clamp(2rem, 5vw, 3.4rem);
          font-weight: 800;
          line-height: 1.1;
          margin: 0 0 1.1rem;
          background: linear-gradient(90deg, var(--sl-accent), #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          display: block;
        }

        /* description */
        .sl-desc {
          font-size: clamp(0.92rem, 1.5vw, 1.05rem);
          color: rgba(255,255,255,0.72);
          line-height: 1.75;
          margin-bottom: 2rem;
          max-width: 560px;
        }
        .sl-content-wrap.center .sl-desc { margin-left: auto; margin-right: auto; }

        /* CTA buttons */
        .sl-ctas {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
        }
        .sl-content-wrap.center .sl-ctas { justify-content: center; }

        .sl-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.7rem 1.5rem;
          background: var(--sl-accent);
          color: #040e1e;
          font-family: var(--font-body);
          font-size: 0.9rem;
          font-weight: 700;
          border-radius: 8px;
          text-decoration: none;
          letter-spacing: 0.01em;
          transition: box-shadow 0.25s, opacity 0.2s, transform 0.2s;
        }
        .sl-btn-primary:hover {
          opacity: 0.9;
          box-shadow: 0 0 24px rgba(0,194,255,0.45);
          transform: translateY(-2px);
          color: #040e1e;
        }
        .sl-btn-primary svg { font-size: 0.75rem; transition: transform 0.2s; }
        .sl-btn-primary:hover svg { transform: translateX(3px); }

        .sl-btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.68rem 1.4rem;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.22);
          color: rgba(255,255,255,0.88);
          font-family: var(--font-body);
          font-size: 0.9rem;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
          backdrop-filter: blur(6px);
        }
        .sl-btn-ghost:hover {
          background: rgba(255,255,255,0.14);
          border-color: rgba(255,255,255,0.4);
          color: #fff;
          transform: translateY(-2px);
        }

        /* ── Bottom bar: indicators + controls ── */
        .sl-bottom {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 6vw;
          background: linear-gradient(to top, rgba(5,15,35,0.65), transparent);
        }

        /* progress indicators */
        .sl-indicators {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .sl-dot {
          position: relative;
          height: 3px;
          border-radius: 3px;
          background: rgba(255,255,255,0.25);
          overflow: hidden;
          cursor: pointer;
          transition: width 0.3s ease, background 0.3s;
        }
        .sl-dot.inactive { width: 20px; }
        .sl-dot.active   { width: 52px; background: rgba(255,255,255,0.35); }
        .sl-dot-fill {
          position: absolute;
          inset: 0;
          background: var(--sl-accent);
          transform: scaleX(0);
          transform-origin: left;
        }
        .sl-dot.active:not(.paused) .sl-dot-fill {
          animation: sl-fill linear var(--sl-interval, 6000ms) forwards;
        }
        @keyframes sl-fill { from { transform: scaleX(0); } to { transform: scaleX(1); } }

        /* slide counter */
        .sl-counter {
          font-size: 0.78rem;
          font-weight: 600;
          color: rgba(255,255,255,0.55);
          letter-spacing: 0.06em;
          min-width: 50px;
        }
        .sl-counter span { color: #fff; }

        /* arrow controls */
        .sl-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .sl-ctrl-btn {
          width: 38px; height: 38px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.75);
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
          backdrop-filter: blur(6px);
        }
        .sl-ctrl-btn:hover {
          background: rgba(255,255,255,0.16);
          color: #fff;
          border-color: rgba(255,255,255,0.35);
        }
        .sl-ctrl-btn:disabled { opacity: 0.35; cursor: not-allowed; }

        /* pause btn */
        .sl-pause-btn {
          width: 38px; height: 38px;
          border-radius: 8px;
          border: 1px solid rgba(0,194,255,0.3);
          background: rgba(0,194,255,0.1);
          color: var(--sl-accent);
          font-size: 0.7rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
          margin-left: 0.25rem;
        }
        .sl-pause-btn:hover { background: rgba(0,194,255,0.2); }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .sl-root        { height: 85vh; min-height: 500px; }
          .sl-content-wrap{ padding: 0 1.5rem; padding-bottom: 100px; }
          .sl-bottom      { padding: 1rem 1.5rem; }
          .sl-desc        { font-size: 0.9rem; }
          .sl-btn-primary,
          .sl-btn-ghost   { font-size: 0.82rem; padding: 0.6rem 1.1rem; }
        }
        @media (max-width: 480px) {
          .sl-root        { height: 90vh; }
          .sl-ctas        { flex-direction: column; align-items: flex-start; }
          .sl-content-wrap.center .sl-ctas { align-items: center; }
          .sl-title,
          .sl-title-accent{ font-size: 1.75rem; }
        }
      `}</style>

      <section
        className="sl-root"
        aria-label="Hero carousel"
        aria-roledescription="carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* ── Slide layers ── */}
        {SLIDES.map((s, i) => (
          <div
            key={s.key}
            className={`sl-slide ${i === current ? "entering" : i === prev ? "leaving" : ""}`}
            aria-hidden={i !== current}
          >
            <div
              className="sl-bg"
              style={{ backgroundImage: `url(${s.img})` }}
              role="img"
              aria-label={`${s.eyebrow} background`}
            />
            <div className="sl-overlay" />
            <div className="sl-vignette" />
          </div>
        ))}

        {/* ── Active slide content ── */}
        <div
          className={`sl-content-wrap ${slide.align}`}
          aria-live="polite"
        >
          <div className={`sl-content ${visible ? "visible" : ""}`} key={slide.key}>
            <div className="sl-eyebrow">{slide.eyebrow}</div>
            <h1 className="sl-title">{slide.title}</h1>
            <span className="sl-title-accent">{slide.titleAccent}</span>
            <p className="sl-desc">{slide.desc}</p>
            <div className="sl-ctas">
              <Link to={slide.cta.to} className="sl-btn-primary">
                {slide.cta.label} <FaArrowRight />
              </Link>
              <Link to={slide.ctaSecondary.to} className="sl-btn-ghost">
                {slide.ctaSecondary.label}
              </Link>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="sl-bottom">

          {/* Counter + dots */}
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <div className="sl-counter">
              <span>{String(current + 1).padStart(2, "0")}</span>
              {" / "}
              {String(SLIDES.length).padStart(2, "0")}
            </div>
            <div className="sl-indicators" role="tablist" aria-label="Slide navigation">
              {SLIDES.map((s, i) => (
                <button
                  key={s.key}
                  className={`sl-dot ${i === current ? "active" : "inactive"} ${paused ? "paused" : ""}`}
                  style={{ "--sl-interval": `${INTERVAL}ms` }}
                  onClick={() => goTo(i)}
                  role="tab"
                  aria-selected={i === current}
                  aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`}
                  title={s.eyebrow}
                >
                  <span className="sl-dot-fill" />
                </button>
              ))}
            </div>
          </div>

          {/* Prev / Pause / Next */}
          <div className="sl-controls">
            <button
              className="sl-ctrl-btn"
              onClick={goPrev}
              aria-label="Previous slide"
            >
              <FaChevronLeft />
            </button>
            <button
              className="sl-pause-btn"
              onClick={() => setPaused((v) => !v)}
              aria-label={paused ? "Resume autoplay" : "Pause autoplay"}
              aria-pressed={paused}
            >
              {paused ? <FaPlay /> : <FaPause />}
            </button>
            <button
              className="sl-ctrl-btn"
              onClick={goNext}
              aria-label="Next slide"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default Slider;